import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import queryString from 'query-string';
import { valueToBigNumber, BigNumber, InterestRate } from '@aave/protocol-js';

import NoDataPanel from '../../../../components/NoDataPanel';
import MarketLimitTooltip from '../../../../components/MarketLimitTooltip';
import BasicForm from '../../../../components/forms/BasicForm';
import routeParamValidationHOC, {
  ValidationWrapperComponentProps,
} from '../../../../components/RouteParamsValidationWrapper';
import BorrowInterestRateForm from '../../components/BorrowInterestRateForm';
import BorrowCurrencyWrapper from '../../components/BorrowCurrencyWrapper';

import { useTxBuilderContext } from '../../../../libs/tx-provider';
import { getAssetInfo } from '../../../../helpers/config/assets-config';
import {
  BorrowUnavailableReason,
  getBorrowUnavailableReason,
  getMaxAmountToBorrow,
} from '../../../../helpers/market-limits';

import messages from './messages';
import staticStyles from './style';

enum BorrowStep {
  AmountForm,
  RateModeSelection,
}

interface BorrowAmountProps
  extends Pick<
    ValidationWrapperComponentProps,
    'userReserve' | 'poolReserve' | 'user' | 'currencySymbol' | 'history'
  > {}

function BorrowAmount({
  userReserve,
  poolReserve,
  user,
  currencySymbol,
  history,
}: BorrowAmountProps) {
  const [amountToBorrow, setAmountToBorrow] = useState('0');
  const [borrowStep, setBorrowStep] = useState<BorrowStep>(BorrowStep.AmountForm);
  const intl = useIntl();
  const { lendingPool } = useTxBuilderContext();

  const asset = getAssetInfo(currencySymbol);

  const maxUserAmountToBorrow = valueToBigNumber(
    user?.availableBorrowsMarketReferenceCurrency || 0
  ).div(poolReserve.priceInMarketReferenceCurrency);
  let rawMaxAmountToBorrow = BigNumber.max(
    BigNumber.min(poolReserve.availableLiquidity, maxUserAmountToBorrow),
    0
  );
  if (
    rawMaxAmountToBorrow.gt(0) &&
    user?.totalBorrowsMarketReferenceCurrency !== '0' &&
    maxUserAmountToBorrow.lt(valueToBigNumber(poolReserve.availableLiquidity).multipliedBy('1.01'))
  ) {
    rawMaxAmountToBorrow = rawMaxAmountToBorrow.multipliedBy('0.99');
  }
  const maxAmountToBorrow = getMaxAmountToBorrow(rawMaxAmountToBorrow, poolReserve, userReserve);
  const formattedMaxAmountToBorrow = maxAmountToBorrow.toString(10);
  const borrowUnavailableReason = user
    ? getBorrowUnavailableReason({
        rawMaxAmountToBorrow,
        maxAmountToBorrow,
        currentLoanToValue: user.currentLoanToValue,
        healthFactor: user.healthFactor,
        totalBorrowsMarketReferenceCurrency: user.totalBorrowsMarketReferenceCurrency,
      })
    : undefined;
  const shouldShowDepositButton =
    !!user && rawMaxAmountToBorrow.eq(0) && valueToBigNumber(poolReserve.baseLTVasCollateral).gt(0);

  const handleSetAmountSubmit = (amount: string) => {
    setAmountToBorrow(amount);
    setBorrowStep(BorrowStep.RateModeSelection);
  };

  const handleInterestModeSubmit = (rateMode: string) => {
    const query = queryString.stringify({ rateMode, amount: amountToBorrow });
    history.push(`${history.location.pathname}/confirmation?${query}`);
  };

  const goBack = () => setBorrowStep(BorrowStep.AmountForm);

  const handleTransactionData = (userId: string) => async () => {
    const referralCode = undefined;
    return await lendingPool.borrow({
      interestRateMode: InterestRate.Variable,
      referralCode,
      user: userId,
      amount: formattedMaxAmountToBorrow,
      reserve: poolReserve.underlyingAsset,
      debtTokenAddress: poolReserve.variableDebtTokenAddress,
    });
  };

  return (
    <BorrowCurrencyWrapper
      poolReserve={poolReserve}
      currencySymbol={currencySymbol}
      userReserve={userReserve}
      user={user}
      goBack={borrowStep === BorrowStep.RateModeSelection ? () => goBack() : undefined}
    >
      {formattedMaxAmountToBorrow !== '0' ? (
        <>
          {borrowStep === BorrowStep.AmountForm && (
            <BasicForm
              title={intl.formatMessage(messages.title)}
              description={intl.formatMessage(messages.description)}
              maxAmount={formattedMaxAmountToBorrow}
              currencySymbol={currencySymbol}
              onSubmit={handleSetAmountSubmit}
              amountFieldTitle={intl.formatMessage(messages.amountTitle)}
              withRiskBar={true}
              maxDecimals={poolReserve.decimals}
              getTransactionData={handleTransactionData}
            />
          )}

          {borrowStep === BorrowStep.RateModeSelection && (
            <BorrowInterestRateForm
              poolReserve={poolReserve}
              userReserve={userReserve}
              amountToBorrow={amountToBorrow}
              onSubmit={handleInterestModeSubmit}
            />
          )}
        </>
      ) : (
        <NoDataPanel
          title={
            !user
              ? intl.formatMessage(messages.connectWallet)
              : poolReserve.availableLiquidity === '0'
              ? intl.formatMessage(messages.noLiquidityAvailableTitle)
              : !user || user.totalLiquidityMarketReferenceCurrency === '0'
              ? intl.formatMessage(messages.noDataTitle)
              : borrowUnavailableReason === BorrowUnavailableReason.ZeroLoanToValue
              ? intl.formatMessage(messages.borrowingPowerUnavailableTitle)
              : borrowUnavailableReason === BorrowUnavailableReason.BorrowingPowerFullyUsed
              ? intl.formatMessage(messages.borrowingPowerFullyUsedTitle)
              : borrowUnavailableReason === BorrowUnavailableReason.HealthFactorTooLow
              ? intl.formatMessage(messages.healthFactorTooLowTitle)
              : intl.formatMessage(messages.exceedLimitTitle)
          }
          description={
            !user
              ? intl.formatMessage(messages.connectWalletDescription)
              : poolReserve.availableLiquidity === '0'
              ? intl.formatMessage(messages.noLiquidityAvailableDescription, {
                  symbol: asset.formattedName,
                })
              : !user || user.totalLiquidityMarketReferenceCurrency === '0'
              ? intl.formatMessage(messages.noDataDescription)
              : borrowUnavailableReason === BorrowUnavailableReason.ZeroLoanToValue
              ? intl.formatMessage(messages.borrowingPowerUnavailableDescription)
              : borrowUnavailableReason === BorrowUnavailableReason.BorrowingPowerFullyUsed
              ? intl.formatMessage(messages.borrowingPowerFullyUsedDescription)
              : borrowUnavailableReason === BorrowUnavailableReason.HealthFactorTooLow
              ? intl.formatMessage(messages.healthFactorTooLowDescription)
              : [
                  <div className="BorrowAmount__marketLimitTooltip">
                    <MarketLimitTooltip {...poolReserve} />
                  </div>,
                ]
          }
          buttonTitle={
            shouldShowDepositButton ? intl.formatMessage(messages.noDataButtonTitle) : undefined
          }
          linkTo={
            shouldShowDepositButton
              ? `/deposit/${poolReserve.underlyingAsset}-${poolReserve.id}`
              : undefined
          }
          withConnectButton={!user}
        />
      )}

      <style jsx={true} global={true}>
        {staticStyles}
      </style>
    </BorrowCurrencyWrapper>
  );
}

export default routeParamValidationHOC({})(BorrowAmount);
